<script setup lang="ts">
import { router, usePage } from '@inertiajs/vue3'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  Link2,
  Link2Off,
  PauseCircle,
  PlayCircle,
  Plus,
  Trash2,
  Webhook,
  Zap,
} from 'lucide-vue-next'
import { ref, watch, watchEffect } from 'vue'

import AppAlert from '@/components/AppAlert.vue'
import AppDialog from '@/components/AppDialog.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'
import { useDialog } from '@/composables/useDialog'
import {
  callbackSettingsPodiumOauthPath,
  connectSettingsPodiumOauthPath,
  settingsPodiumOauthPath,
  settingsPodiumWebhookPath,
  settingsPodiumWebhooksPath,
  toggleSettingsPodiumWebhookPath,
  webhooksPodiumPath,
} from '@/routes'

// ── Types ─────────────────────────────────────────────────────────────────────

interface PodiumWebhook {
  uid: string
  url: string
  eventTypes: string[]
  disabled: boolean
  secret?: string
  createdAt: string
  updatedAt: string
}

interface NewWebhook {
  uid: string
  url: string
  secret: string
}

interface Props {
  webhooks: PodiumWebhook[] | null // null = fetch failed
  eventTypeGroups: Record<string, string[]>
  connected: boolean
}

defineProps<Props>()

const page = usePage()

// ── Copy to clipboard ─────────────────────────────────────────────────────────

const { copy, isCopied } = useCopyToClipboard()

// ── OAuth callback URL ────────────────────────────────────────────────────────

const callbackUrl = window.location.origin + callbackSettingsPodiumOauthPath()

// ── New webhook secret reveal ─────────────────────────────────────────────────

const showSecretDialog = ref(false)
const revealedWebhook = ref<NewWebhook | null>(null)

watch(
  () => (page.flash as Record<string, unknown> | undefined)?.new_webhook as NewWebhook | undefined,
  (wh) => {
    if (wh) {
      revealedWebhook.value = wh
      showSecretDialog.value = true
    }
  },
  { immediate: true },
)

function copySecret() {
  if (!revealedWebhook.value) return
  copy(revealedWebhook.value.secret, 'secret')
}

// ── Create form ───────────────────────────────────────────────────────────────

const showCreateDialog = ref(false)
const webhookUrl = ref('')
const selectedEvents = ref<string[]>([])
const creating = ref(false)
const urlError = ref<string | null>(null)

// Clear URL error as the user corrects the input
watchEffect(() => {
  if (urlError.value) urlError.value = validateWebhookUrl(webhookUrl.value)
})

const defaultMessageEvents = ['message.received', 'message.sent', 'message.failed']

function openCreate() {
  webhookUrl.value = window.location.origin + webhooksPodiumPath()
  selectedEvents.value = [...defaultMessageEvents]
  urlError.value = null
  showCreateDialog.value = true
}

function toggleGroup(events: string[]) {
  const allSelected = events.every((e) => selectedEvents.value.includes(e))
  if (allSelected) {
    selectedEvents.value = selectedEvents.value.filter((e) => !events.includes(e))
  } else {
    const toAdd = events.filter((e) => !selectedEvents.value.includes(e))
    selectedEvents.value = [...selectedEvents.value, ...toAdd]
  }
}

function isGroupChecked(events: string[]): boolean {
  return events.every((e) => selectedEvents.value.includes(e))
}

function isGroupIndeterminate(events: string[]): boolean {
  const checked = events.filter((e) => selectedEvents.value.includes(e))
  return checked.length > 0 && checked.length < events.length
}

function toggleEvent(event: string) {
  if (selectedEvents.value.includes(event)) {
    selectedEvents.value = selectedEvents.value.filter((e) => e !== event)
  } else {
    selectedEvents.value = [...selectedEvents.value, event]
  }
}

function validateWebhookUrl(raw: string): string | null {
  const url = raw.trim()
  if (!url) return 'URL is required'
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') return 'URL must use HTTPS (https://…)'
    if (!parsed.hostname) return 'Enter a valid URL'
  } catch {
    return 'Enter a valid URL'
  }
  return null
}

function submitCreate() {
  const urlValidation = validateWebhookUrl(webhookUrl.value)
  if (urlValidation) {
    urlError.value = urlValidation
    return
  }
  if (selectedEvents.value.length === 0) {
    urlError.value = 'Select at least one event type'
    return
  }
  urlError.value = null
  creating.value = true
  router.post(
    settingsPodiumWebhooksPath(),
    { url: webhookUrl.value.trim(), event_types: selectedEvents.value },
    {
      onSuccess: () => {
        showCreateDialog.value = false
      },
      onFinish: () => {
        creating.value = false
      },
    },
  )
}

// ── Delete ────────────────────────────────────────────────────────────────────
const { confirm } = useDialog()

async function confirmDelete(wh: PodiumWebhook) {
  const ok = await confirm({
    title: 'Delete webhook?',
    description: `Remove this webhook from Podium? Events will stop being delivered immediately.\n${wh.url}`,
    confirmLabel: 'Delete',
  })
  if (!ok) return
  router.delete(settingsPodiumWebhookPath(wh.uid))
}

// ── Toggle (enable / disable) ─────────────────────────────────────────────────

function toggleWebhook(wh: PodiumWebhook) {
  router.patch(toggleSettingsPodiumWebhookPath(wh.uid), { disabled: !wh.disabled })
}

// ── Collapsed event list ──────────────────────────────────────────────────────

const expandedUids = ref<Set<string>>(new Set())

function toggleExpand(uid: string) {
  if (expandedUids.value.has(uid)) {
    expandedUids.value.delete(uid)
  } else {
    expandedUids.value.add(uid)
  }
  // Trigger reactivity
  expandedUids.value = new Set(expandedUids.value)
}

function formatEvent(e: string) {
  return e.replace('.', ' › ')
}
</script>

<template>
  <Card>
    <CardHeader>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Webhook class="text-muted-foreground size-5" />
          <div>
            <CardTitle class="text-base">Podium Webhooks</CardTitle>
            <CardDescription>
              Register webhook endpoints with Podium to receive real-time SMS and contact events.
            </CardDescription>
          </div>
        </div>
        <Button size="sm" @click="openCreate">
          <Plus class="size-4" />
          Register Webhook
        </Button>
      </div>
    </CardHeader>

    <!-- Connection status bar -->
    <div class="bg-muted/30 flex items-center justify-between gap-4 border-t px-6 py-3">
      <div class="flex items-center gap-2 text-sm">
        <template v-if="connected">
          <Link2 class="size-4 shrink-0 text-emerald-500" />
          <span class="font-medium text-emerald-700 dark:text-emerald-400">Connected to Podium</span>
        </template>
        <template v-else>
          <Link2Off class="size-4 shrink-0 text-amber-500" />
          <span class="font-medium text-amber-700 dark:text-amber-400">Not connected to Podium</span>
        </template>
      </div>
      <template v-if="connected">
        <Button
          variant="ghost"
          size="sm"
          class="text-muted-foreground hover:text-destructive"
          @click="router.delete(settingsPodiumOauthPath())"
        >
          Disconnect
        </Button>
      </template>
      <template v-else>
        <Button size="sm" as="a" :href="connectSettingsPodiumOauthPath()">
          <Link2 class="size-4" />
          Connect with Podium
        </Button>
      </template>
    </div>

    <!-- OAuth callback URL (for Podium Developer Portal setup) -->
    <div class="space-y-1.5 border-t px-6 py-3">
      <p class="text-muted-foreground text-xs">OAuth Callback URL</p>
      <div class="bg-muted/50 flex items-center gap-2 rounded-md border px-3 py-2">
        <code class="text-muted-foreground flex-1 truncate font-mono text-xs select-all">{{ callbackUrl }}</code>
        <Button
          variant="ghost"
          size="icon"
          class="text-muted-foreground hover:text-foreground size-6 shrink-0"
          title="Copy callback URL"
          @click="copy(callbackUrl, 'callback')"
        >
          <Check v-if="isCopied('callback')" class="size-3 text-emerald-500" />
          <Copy v-else class="size-3" />
        </Button>
      </div>
    </div>

    <CardContent class="p-0">
      <!-- Fetch error -->
      <div
        v-if="webhooks === null"
        class="flex items-center gap-2 px-6 py-4 text-sm text-amber-600 dark:text-amber-400"
      >
        <AlertTriangle class="size-4 shrink-0" />
        <span>Could not load webhooks from Podium — check that Podium credentials are configured.</span>
      </div>

      <!-- Empty -->
      <div v-else-if="webhooks.length === 0" class="flex flex-col items-center gap-2 py-12 text-center">
        <Webhook class="text-muted-foreground/40 size-10" />
        <p class="text-muted-foreground text-sm">No webhooks registered</p>
        <p class="text-muted-foreground/70 text-xs">Register a webhook so Podium sends inbound messages to this app.</p>
      </div>

      <!-- Webhook list -->
      <div v-else class="divide-border divide-y">
        <div v-for="wh in webhooks" :key="wh.uid" class="px-6 py-4">
          <div class="flex items-start justify-between gap-4">
            <!-- Left: URL + events -->
            <div class="min-w-0 flex-1 space-y-1.5">
              <div class="flex items-center gap-2">
                <Badge :variant="wh.disabled ? 'secondary' : 'default'" class="shrink-0 text-[10px]">
                  {{ wh.disabled ? 'Disabled' : 'Active' }}
                </Badge>
                <code class="text-foreground truncate font-mono text-xs">{{ wh.url }}</code>
              </div>

              <!-- Event list (collapsible) -->
              <div>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
                  @click="toggleExpand(wh.uid)"
                >
                  <Zap class="size-3" />
                  {{ wh.eventTypes.length }} event{{ wh.eventTypes.length === 1 ? '' : 's' }}
                  <ChevronDown v-if="!expandedUids.has(wh.uid)" class="size-3" />
                  <ChevronUp v-else class="size-3" />
                </button>

                <div v-if="expandedUids.has(wh.uid)" class="mt-1.5 flex flex-wrap gap-1">
                  <Badge v-for="evt in wh.eventTypes" :key="evt" variant="outline" class="font-mono text-[10px]">
                    {{ formatEvent(evt) }}
                  </Badge>
                </div>
              </div>

              <p class="text-muted-foreground text-[10px]">UID: {{ wh.uid }}</p>
            </div>

            <!-- Right: actions -->
            <div class="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                class="size-8"
                :title="wh.disabled ? 'Enable webhook' : 'Disable webhook'"
                @click="toggleWebhook(wh)"
              >
                <PauseCircle v-if="!wh.disabled" class="text-muted-foreground size-4" />
                <PlayCircle v-else class="text-muted-foreground size-4" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                class="text-destructive hover:text-destructive size-8"
                title="Delete webhook"
                @click="confirmDelete(wh)"
              >
                <Trash2 class="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>

  <!-- ── Register webhook dialog ─────────────────────────────────────────── -->
  <AppDialog
    v-model:open="showCreateDialog"
    title="Register Podium Webhook"
    description="Podium will POST events to this URL. Use your app's public domain."
    :loading="creating"
    loading-message="Registering webhook…"
    class="sm:max-w-2xl"
  >
    <div class="space-y-6">
      <!-- URL -->
      <div class="grid gap-2">
        <Label for="wh-url">Endpoint URL</Label>
        <Input
          id="wh-url"
          v-model="webhookUrl"
          type="url"
          placeholder="https://your-app.com/webhooks/podium"
          autofocus
        />
        <p v-if="urlError" class="text-destructive text-xs">{{ urlError }}</p>
        <p v-else class="text-muted-foreground text-xs">
          Pre-filled with your current domain. Update if deploying to a different host.
        </p>
      </div>

      <!-- Event type groups -->
      <div class="grid gap-2">
        <Label>Events to subscribe</Label>
        <div class="overflow-hidden rounded-lg border">
          <div v-for="(events, group) in eventTypeGroups" :key="group" class="border-b last:border-b-0">
            <!-- Group header -->
            <Label
              class="bg-muted/40 hover:bg-muted/70 flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors"
            >
              <Checkbox
                :modelValue="isGroupIndeterminate(events) ? 'indeterminate' : isGroupChecked(events)"
                @update:modelValue="toggleGroup(events)"
              />
              <span class="text-foreground text-sm font-semibold">{{ group }}</span>
              <span class="text-muted-foreground ml-auto text-xs font-normal">{{ events.length }} events</span>
            </Label>

            <!-- Individual events -->
            <div class="divide-border grid grid-cols-2 divide-x">
              <Label
                v-for="evt in events"
                :key="evt"
                class="hover:bg-muted/30 border-border/50 flex cursor-pointer items-center gap-2.5 border-b px-4 py-2 transition-colors last:border-b-0 [&:nth-last-child(2)]:border-b-0"
              >
                <Checkbox :modelValue="selectedEvents.includes(evt)" @update:modelValue="toggleEvent(evt)" />
                <span class="text-foreground font-mono text-xs">{{ formatEvent(evt) }}</span>
              </Label>
            </div>
          </div>
        </div>
        <p class="text-muted-foreground text-xs">Message events are pre-selected — they power the in-app chat.</p>
      </div>
    </div>
    <template #footer>
      <Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
      <Button :disabled="creating" @click="submitCreate">Register</Button>
    </template>
  </AppDialog>

  <!-- ── Secret reveal dialog (one-time) ────────────────────────────────── -->
  <Dialog v-model:open="showSecretDialog">
    <DialogContent class="sm:max-w-md [&>button:last-child]:hidden" @interact-outside.prevent @escape-key-down.prevent>
      <DialogHeader>
        <DialogTitle>Webhook Registered</DialogTitle>
        <DialogDescription>
          Copy the signing secret now — it will <strong>not</strong> be shown again. Set it as
          <code class="bg-muted rounded px-1 py-0.5 text-xs">PODIUM_WEBHOOK_SECRET</code> in your environment.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4">
        <!-- URL -->
        <div class="space-y-1">
          <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Webhook URL</p>
          <code class="bg-muted block rounded px-3 py-2 text-xs break-all">{{ callbackUrl }}</code>
        </div>

        <!-- Secret -->
        <div class="space-y-1">
          <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Signing Secret</p>
          <div class="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
            <Eye class="text-muted-foreground size-4 shrink-0" />
            <code class="flex-1 text-xs break-all">{{ revealedWebhook?.secret }}</code>
            <Button variant="ghost" size="icon" class="size-7 shrink-0" @click="copySecret">
              <Check v-if="isCopied('secret')" class="size-3.5 text-emerald-500" />
              <Copy v-else class="size-3.5" />
            </Button>
          </div>
          <p v-if="isCopied('secret')" class="text-muted-foreground text-center text-xs">Copied!</p>
        </div>

        <AppAlert variant="warning" class="px-3 py-2 text-xs">
          Redeploy with <code class="font-mono">PODIUM_WEBHOOK_SECRET</code> set before inbound messages will be
          accepted.
        </AppAlert>
      </div>

      <DialogFooter>
        <Button @click="showSecretDialog = false">Done</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

</template>
